import React, { useState } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { bailleurApi } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { ArrowLeft, Building2, KeyRound, LockKeyhole, ShieldCheck, UserRound } from 'lucide-react';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

const LOGO_URL = 'https://customer-assets-wrfwihn1.emergentagent.net/job_aac32766-5fa5-4a83-81c9-03bafeaebc9e/artifacts/bi1rz20k_loyerbook.png';

export default function Login() {
  const [sp] = useSearchParams();
  const params = useParams();
  const { t } = useTranslation();
  const initialRole = params.role || sp.get('role') || null;
  const [selectedRole, setSelectedRole] = useState(initialRole === 'locataire' || initialRole === 'bailleur' ? initialRole : null);
  const roleLabel = selectedRole === 'locataire' ? t('landing.locataire') : selectedRole === 'bailleur' ? t('landing.bailleur') : t('auth.space');
  const { login, completeMfa } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [challengeToken, setChallengeToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  const routeAfterLogin = async (user) => {
    if (user.must_change_password) { navigate('/change-password'); return; }
    if (user.role === 'platform_owner' || user.role === 'admin') { navigate('/admin'); return; }
    if (user.role === 'agent') { navigate('/agent'); return; }
    if (user.role === 'locataire') { navigate('/locataire'); return; }
    if (user.role === 'bailleur') {
      try {
        const { data } = await bailleurApi.subscription();
        const subscription = data?.subscription;
        if (!subscription || subscription.status === 'pending_payment' || subscription.payment_method === 'pending') {
          navigate('/bailleur/abonnement', { replace: true });
          return;
        }
      } catch {
        // If subscription inspection is unavailable, the backend still enforces
        // billing access on protected routes. Send the user to the billing page.
        navigate('/bailleur/abonnement', { replace: true });
        return;
      }
      navigate('/bailleur');
      return;
    }
    navigate('/');
  };

  const chooseRole = (role) => {
    setSelectedRole(role);
    setChallengeToken(null);
    setMfaCode('');
    setIdentifier('');
    setPassword('');
    navigate(`/connexion/${role}`, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRole) return toast.error('Choisissez votre espace de connexion.');
    if (!identifier || !password) return;
    setLoading(true);
    try {
      const res = await login(identifier, password, selectedRole);
      if (res.mfa_required) {
        setChallengeToken(res.challenge_token);
        toast.info('Vérification en 2 étapes requise');
      } else {
        toast.success('Bienvenue sur LoyerBook');
        await routeAfterLogin(res.user);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Identifiants incorrects';
      toast.error(typeof msg === 'string' ? msg : msg?.message || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  const onMfaSubmit = async (e) => {
    e.preventDefault();
    if (!mfaCode || mfaCode.length < 6) return;
    setLoading(true);
    try {
      const user = await completeMfa(challengeToken, mfaCode);
      toast.success('Authentification 2 étapes réussie');
      await routeAfterLogin(user);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Code MFA invalide');
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  };

  const showMfa = Boolean(challengeToken);

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex lb-hero-bg text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 lb-dots opacity-50" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="relative"><div className="absolute inset-0 lb-logo-pulse" /><img src={LOGO_URL} alt="LoyerBook" className="relative h-14 w-14 bg-white rounded-lg p-1 lb-logo-anim" /></div>
          <div><div className="text-2xl font-extrabold tracking-tight">LoyerBook</div><div className="text-[11px] uppercase tracking-[0.25em] text-[#7BA7E8]">{t('app.tagline')}</div></div>
        </div>
        <div className="relative z-10"><div className="text-[11px] uppercase tracking-[0.25em] text-[#7BA7E8] mb-4">Un accès, deux espaces</div><h2 className="text-4xl font-extrabold leading-tight">Bailleur ou locataire, connectez-vous au bon espace.</h2><p className="mt-4 text-slate-300 max-w-md">Le bailleur gère ses biens, loyers et locataires. Le locataire consulte ses échéances, paiements et reçus.</p></div>
        <div className="relative z-10 text-xs text-slate-500">© {new Date().getFullYear()} LoyerBook</div>
      </div>

      <div className="flex items-center justify-center p-4 sm:p-10 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8"><Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0D1B2A]"><ArrowLeft className="w-4 h-4" /> {t('auth.backHome')}</Link><LanguageSwitcher testid="login-lang-switch" /></div>

          {!selectedRole && !showMfa && (
            <div>
              <div className="mb-8"><div className="inline-flex items-center gap-2 rounded-full bg-[#E6F1FF] px-3 py-1 text-xs font-semibold text-[#1677FF] mb-4"><LockKeyhole className="w-3 h-3" /> Connexion LoyerBook</div><h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0D1B2A]">Choisissez votre espace</h1><p className="mt-2 text-sm text-slate-500">Un seul bouton Connexion sur le site, puis choisissez le profil qui correspond à votre compte.</p></div>
              <div className="grid gap-4">
                <button type="button" onClick={() => chooseRole('bailleur')} className="group rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md">
                  <div className="flex items-start gap-4"><div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Building2 className="h-6 w-6" /></div><div><div className="text-lg font-black text-slate-900">Espace bailleur</div><div className="mt-1 text-sm leading-relaxed text-slate-500">Gérez vos locataires, loyers, échéances, paiements, reçus et abonnement.</div><div className="mt-3 text-sm font-bold text-blue-600 group-hover:underline">Se connecter comme bailleur →</div></div></div>
                </button>
                <button type="button" onClick={() => chooseRole('locataire')} className="group rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:border-emerald-300 hover:shadow-md">
                  <div className="flex items-start gap-4"><div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><UserRound className="h-6 w-6" /></div><div><div className="text-lg font-black text-slate-900">Espace locataire</div><div className="mt-1 text-sm leading-relaxed text-slate-500">Consultez vos loyers, échéances, paiements enregistrés et reçus.</div><div className="mt-3 text-sm font-bold text-emerald-600 group-hover:underline">Se connecter comme locataire →</div></div></div>
                </button>
              </div>
              <div className="mt-6 text-center text-sm text-slate-500">Vous êtes bailleur et vous n’avez pas encore de compte ? <Link to="/inscription" className="font-bold text-blue-600 hover:underline">Créer un compte</Link></div>
            </div>
          )}

          {selectedRole && !showMfa && (
            <>
              <div className="mb-8"><button type="button" onClick={() => { setSelectedRole(null); navigate('/login', { replace: true }); }} className="mb-4 text-sm font-semibold text-slate-500 hover:text-blue-600">← Changer d’espace</button><div className="inline-flex items-center gap-2 rounded-full bg-[#E6F1FF] px-3 py-1 text-xs font-semibold text-[#1677FF] mb-4"><LockKeyhole className="w-3 h-3" /> {t('auth.space')} {roleLabel}</div><h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0D1B2A]">{t('auth.loginTitle')}</h1><p className="mt-2 text-sm text-slate-500">{selectedRole === 'bailleur' ? 'Accédez à votre espace de gestion locative.' : 'Accédez à votre espace locataire et à vos informations de loyer.'}</p></div>
              <form data-testid={TID.loginForm} onSubmit={onSubmit} className="space-y-5">
                <div><Label htmlFor="identifier" className="text-[#0D1B2A] font-medium">{t('auth.identifierLabel')}</Label><Input id="identifier" data-testid={TID.loginIdentifier} value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="paul@example.com" className="mt-1.5 h-12" autoComplete="username" required /></div>
                <div><Label htmlFor="password" className="text-[#0D1B2A] font-medium">{t('auth.passwordLabel')}</Label><Input id="password" data-testid={TID.loginPassword} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 h-12" autoComplete="current-password" required /></div>
                <Button type="submit" data-testid={TID.loginSubmit} disabled={loading} className="w-full h-12 bg-[#1677FF] hover:bg-[#0E62DB] font-semibold">{loading ? t('auth.loggingIn') : t('auth.loginButton')}</Button>
              </form>
              <div className="mt-8 text-xs text-slate-500 leading-relaxed">{selectedRole === 'locataire' ? t('auth.helpLocataire') : t('auth.helpBailleur')}</div>
            </>
          )}

          {showMfa && (
            <><div className="mb-8"><div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-700 mb-4"><ShieldCheck className="w-3 h-3" /> Vérification en 2 étapes</div><h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0D1B2A]">Code d&apos;authentification</h1><p className="mt-2 text-sm text-slate-500">Ouvrez votre application d&apos;authentification et saisissez le code à 6 chiffres.</p></div><form data-testid="mfa-form" onSubmit={onMfaSubmit} className="space-y-5"><div><Label htmlFor="mfa-code" className="text-[#0D1B2A] font-medium">Code TOTP</Label><Input id="mfa-code" data-testid="mfa-code-input" value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" className="mt-1.5 h-14 text-center text-2xl font-mono tracking-[0.6em] font-bold" autoComplete="one-time-code" inputMode="numeric" autoFocus required /><div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><KeyRound className="w-3 h-3" /> 6 chiffres — renouvelé toutes les 30 secondes.</div></div><div className="flex flex-col-reverse gap-3 sm:flex-row"><Button type="button" variant="outline" onClick={() => { setChallengeToken(null); setMfaCode(''); }} disabled={loading} className="flex-1 h-12">Annuler</Button><Button type="submit" data-testid="mfa-submit" disabled={loading || mfaCode.length !== 6} className="flex-1 h-12 bg-[#1677FF] hover:bg-[#0E62DB] font-semibold">{loading ? 'Vérification…' : 'Vérifier'}</Button></div></form></>
          )}
        </div>
      </div>
    </div>
  );
}
